import { Box, Slide, SlideProps } from "@mui/material";
import { subscribeToGameStream } from "api/game/get-game-stream";
import * as LoadingSpinner from "components/core/LoadingSpinner";
import * as PageWithSidebar from "components/core/PageWithSidebar";
import * as AllPlayerBoards from "components/pages/game/AllPlayerBoards";
import * as ColonyTile from "components/pages/game/ColonyTile";
import * as ChooseAttackTarget from "components/pages/game/decision-components/ChooseAttackTarget";
import * as ChooseCardEffect from "components/pages/game/decision-components/ChooseCardEffect";
import * as ChooseTargetCard from "components/pages/game/decision-components/ChooseTargetCard";
import * as DiscardCard from "components/pages/game/decision-components/DiscardCard";
import * as PlaceTile from "components/pages/game/decision-components/PlaceTile";
import * as PurchaseCardDecision from "components/pages/game/decision-components/PurchaseCardDecision";
import * as SellCards from "components/pages/game/decision-components/SellCards";
import * as TapAnotherCard from "components/pages/game/decision-components/TapAnotherCard";
import * as TilePlacementDecision from "components/pages/game/decision-components/TilePlacementDecision";
import * as TradeWithColonyModal from "components/pages/game/decision-modals/TradeWithColonyModal";
import * as DraftCards from "components/pages/game/DraftCards";
import * as FinishedGameSummary from "components/pages/game/FinishedGameSummary";
import * as GameBoard from "components/pages/game/GameBoard";
import * as GameStatus from "components/pages/game/GameStatus";
import * as HistoryContainer from "components/pages/game/HistoryContainer";
import * as MainActions from "components/pages/game/MainActions";
import * as PlayerBoard from "components/pages/game/PlayerBoard";
import * as PlayerHand from "components/pages/game/PlayerHand";
import * as PlayerSummary from "components/pages/game/PlayerSummary";
import * as SolarActions from "components/pages/game/SolarActions";
import * as StartingHand from "components/pages/game/StartingHand";
import _ from "lodash";
import { ChooseAttackOptionType } from "models/ChooseAttackOptionType";
import { ChooseCardDecisionType } from "models/ChooseCardDecisionType";
import { ChooseEffectOptionType } from "models/ChooseEffectOptionType";
import { GameType } from "models/GameType";
import { SnackbarKey, useSnackbar } from "notistack";
import React, { createContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import * as CardHelpers from "utils/card_helpers";
import { isCurrentPlayersTurn, isMultiplayerGame, isSinglePlayerGame } from "utils/user_helpers";

export const GameContext = createContext<{
  game: GameType;
  setGame: (game: GameType) => void;
}>({
  game: {} as GameType,
  setGame: () => {},
});

export const Component: React.FC<{}> = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const lastSnackbarKeyRef = useRef<SnackbarKey | null>(null);
  const location = useLocation();
  const { gameId } = useParams();
  const [maybeGame, setMaybeGame] = useState<GameType | null>(null);
  const maybeGameRef = useRef<GameType | null>(null);
  const [openActionModalName, setOpenActionModalName] = useState<string>("");
  const [showGameLogSidebar, setShowGameLogSidebar] = useState<boolean>(true);

  useEffect(() => {
    maybeGameRef.current = maybeGame;
    maybeRenderTurnBanner(maybeGame);
  }, [maybeGame]);

  // Remove the banner when the user navigates away
  useEffect(() => {
    return () => {
      if (lastSnackbarKeyRef.current !== null) {
        closeSnackbar(lastSnackbarKeyRef.current);
      }
    };
  }, [location.pathname, closeSnackbar]);

  const maybeRenderTurnBanner = (game: GameType | null) => {
    if (game === null) {
      if (lastSnackbarKeyRef.current !== null) {
        closeSnackbar(lastSnackbarKeyRef.current);
        lastSnackbarKeyRef.current = null;
      }
      return;
    }

    if (isSinglePlayerGame(game)) {
      return;
    }

    if (lastSnackbarKeyRef.current === null && isCurrentPlayersTurn(game)) {
      const key = enqueueSnackbar("It's your turn", {
        variant: "info",
        persist: true,
        style: {
          fontSize: "1.5em",
          textAlign: "center",
          height: "auto",
          padding: "16px",
        },
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
        TransitionComponent: NoTransition,
      });

      lastSnackbarKeyRef.current = key;
    }

    if (lastSnackbarKeyRef.current !== null && !isCurrentPlayersTurn(game)) {
      closeSnackbar(lastSnackbarKeyRef.current);
      lastSnackbarKeyRef.current = null;
    }
  };

  const NoTransition = (props: SlideProps) => {
    return <Slide {...props} in={props.in} direction="down" />;
  };

  useEffect(() => {
    if (!gameId) {
      return;
    }
    const cleanup = subscribeToGameStream({
      gameId,
      onGameUpdate: (game: GameType | null) => {
        if (!_.isEqual(game, maybeGameRef.current)) {
          setMaybeGame(game);
        }
      },
    });
    return () => {
      cleanup();
    };
  }, [gameId]);

  const renderPlayerCardsAndActions = (game: GameType) => {
    switch (game.phase) {
      case "INITIAL":
        return <StartingHand.Component />;
      case "CORE":
        return isCurrentPlayersTurn(game) ? renderCardsAndCoreActions(game) : renderOnlyCards(game);
      case "RESEARCH":
        return renderDraftActions(game);
      case "SOLAR":
        return <SolarActions.Component />;
      case "FINAL_GREENERIES":
        return renderFinalGreeneries(game);
      case "FINAL":
        return renderFinalCardsAndBoard(game);
      default:
        return <div>{`Unimplemented game Phase: ${game.phase}`}</div>;
    }
  };

  const renderOnlyCards = (game: GameType) => {
    return (
      <>
        <PlayerHand.Component
          player={game.player}
          isClickable={game.decisionStack[0]?.UserId === game.player.UserId}
        />
        <PlayerBoard.Component
          cards={game.player.cardsOnBoard}
          canTap={isCurrentPlayersTurn(game)}
        />
      </>
    );
  };

  const renderCardsAndCoreActions = (game: GameType) => {
    return (
      <>
        {game.currentPlayerDecision !== "SELL" &&
        game.decisionStack[0]?.DecisionId !== "DISCARD_CARD" ? (
          <PlayerHand.Component
            player={game.player}
            isClickable={game.decisionStack[0]?.UserId === game.player.UserId}
          />
        ) : null}
        {maybeRenderDecision(game)}
        {game.currentPlayerDecision === "MAIN_ACTION" && isCurrentPlayersTurn(game) ? (
          <MainActions.Component game={game} />
        ) : null}
        {game.currentPlayerDecision === "SELL" ? (
          <SellCards.Component cards={game.player.hand} />
        ) : null}
        <PlayerBoard.Component
          cards={game.player.cardsOnBoard}
          canTap={isCurrentPlayersTurn(game)}
        />
      </>
    );
  };

  const renderDraftActions = (game: GameType) => {
    return (
      <>
        <PlayerHand.Component player={game.player} isClickable={false} />
        <DraftCards.Component />
        <PlayerBoard.Component
          cards={game.player.cardsOnBoard}
          canTap={isCurrentPlayersTurn(game)}
        />
      </>
    );
  };

  const renderFinalGreeneries = (game: GameType) => {
    return (
      <>
        <PlayerHand.Component player={game.player} isClickable={false} />
        <PlaceTile.Component tileName={game.currentPlayerDecisionData?.tile ?? ""} />
        <PlayerBoard.Component cards={game.player.cardsOnBoard} canTap={false} />
      </>
    );
  };

  const renderFinalCardsAndBoard = (game: GameType) => {
    return (
      <>
        <PlayerHand.Component player={game.player} isClickable={false} />
        <PlayerBoard.Component
          cards={game.player.cardsOnBoard}
          canTap={isCurrentPlayersTurn(game)}
        />
      </>
    );
  };

  const renderFinalResult = (game: GameType) => {
    return <FinishedGameSummary.Component game={game} />;
  };

  const maybeRenderDecision = (game: GameType) => {
    if (game.decisionStack.length === 0 || !game.currentPlayerDecisionData) {
      return null;
    }

    const decisionId = game.decisionStack[0]?.DecisionId;
    const decisionData = game.currentPlayerDecisionData;

    if (decisionId === "CHOOSE_CARD_EFFECT") {
      return (
        <ChooseCardEffect.Component
          cardName={decisionData.card}
          options={decisionData.options as ChooseEffectOptionType[]}
          description={decisionData.description}
          optionsAreCards={decisionData.optionsAreCards ?? false}
        />
      );
    }
    if (decisionId === "TARGET_CARD") {
      return (
        <ChooseTargetCard.Component
          options={decisionData.options as ChooseCardDecisionType[]}
          canChooseNone={decisionData.canChooseNone}
        />
      );
    }
    if (decisionId === "DISCARD_CARD") {
      return <DiscardCard.Component cards={game.player.hand} />;
    }
    if (decisionId === "PLACE_TILE") {
      return <PlaceTile.Component tileName={game.currentPlayerDecisionData?.tile ?? ""} />;
    }
    if (decisionId === "CHOOSE_ATTACK_TARGET") {
      return (
        <ChooseAttackTarget.Component options={decisionData.options as ChooseAttackOptionType[]} />
      );
    }
    if (decisionId === "PURCHASE_CARD_DECISION") {
      return (
        <PurchaseCardDecision.Component
          cardName={decisionData.available?.name ?? ""}
          tooltip={decisionData.available?.tooltip ?? ""}
        />
      );
    }
    if (decisionId === "TILE_PLACEMENT_DECISION") {
      return (
        <TilePlacementDecision.Component
          options={decisionData.options as ChooseEffectOptionType[]}
        />
      );
    }
    if (decisionId === "TRADE_COLONY") {
      return (
        <TradeWithColonyModal.Component
          isOpen={openActionModalName === "TRADE_COLONY"}
          onClose={() => setOpenActionModalName("")}
        />
      );
    }

    if (decisionId === "VIRON_TAP") {
      return (
        <TapAnotherCard.Component
          cards={game.player.cardsOnBoard
            .filter((card) => card.hasBeenTapped)
            .filter((card) => card.name !== "Viron")}
        />
      );
    }

    return (
      <Box sx={{ color: "white" }}>
        Unimplemented decisionId {game.decisionStack[0]?.DecisionId}
      </Box>
    );
  };

  const renderGameLogSidebar = (game: GameType) => {
    return <HistoryContainer.Component logContent={game.history} />;
  };

  const renderGame = (game: GameType) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Box sx={{ width: "650px" }}>
            <GameStatus.Component
              oceans={9 - game.oceansRemaining}
              oxygen={game.oxygenSteps}
              temperature={game.temperatureSteps}
              venus={game.venusSteps}
              generation={game.generation}
              milestones={game.milestones}
              awards={game.awards}
              isSinglePlayer={isSinglePlayerGame(game)}
            />

            <GameBoard.Component game={game} />
            {game.colonyTiles.map((colonyTile) => (
              <Box sx={{ display: "flex", flexDirection: "row", width: "300px" }}>
                <ColonyTile.Component colony={colonyTile} />
              </Box>
            ))}
          </Box>
          <Box>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <PlayerSummary.Component
                game={game}
                name={game.player.name}
                isCurrentTurn={game.decisionStack[0]?.UserId === game.player.UserId}
                hasPassedForGen={game.player.passedThisGeneration}
                resources={game.player.resources}
                production={game.player.production}
                events={game.player.cardsOnBoard.filter(CardHelpers.isEventCard).length}
                terraformRating={game.player.terraformRating}
                turnOrder={game.player.orderThisGen}
                playerColor={game.player.color}
                numberOfCardsInHand={game.player.hand.length}
                cardPoints={game.player.cardPoints}
                totalTradeFleets={game.player.totalTradeFleets}
                userId={game.player.UserId}
              />
            </Box>
            {renderPlayerCardsAndActions(game)}
          </Box>
        </Box>
        {game.phase === "FINAL" ? renderFinalResult(game) : null}
        {isMultiplayerGame(game) ? (
          <Box>
            <AllPlayerBoards.Component />
          </Box>
        ) : null}
      </Box>
    );
  };

  if (maybeGame === null) {
    return <LoadingSpinner.Component />;
  }

  return (
    <div>
      <GameContext.Provider value={{ game: maybeGame, setGame: setMaybeGame }}>
        <PageWithSidebar.Component
          children={renderGame(maybeGame)}
          sidebarContent={renderGameLogSidebar(maybeGame)}
          isOpen={showGameLogSidebar}
          onClose={() => setShowGameLogSidebar(false)}
        />
      </GameContext.Provider>
    </div>
  );
};

Component.displayName = "GamePage";
